import {
    i18n, Messages
} from '@lingui/core';
import { en } from 'make-plural/plurals';

import { APP_CONFIG } from '../../config';

export const PSEUDO_LOCALE = 'en-PL';

export const DEFAULT_LOCALE_DATA = {
    [APP_CONFIG.defaultLocale]: { plurals: en },
    [PSEUDO_LOCALE]: { plurals: en }
};

type LoadI18NOptions = {
    locale?: string
    shouldLoadLocaleFromCDN?: boolean
    localisationCdnBaseUrl?: string
};

const DEFAULT_LOCALE_OPTIONS: LoadI18NOptions = {
    locale: APP_CONFIG.defaultLocale,
    shouldLoadLocaleFromCDN: APP_CONFIG.shouldLoadLocaleFromCDN
};

/**
 * Handle loading of the localisation json data manually or from CDN or locally for development.
 * In the APP_CONFIG this can be based on whether this is a production build.
 */
export async function loadI18n ({
    locale = APP_CONFIG.defaultLocale,
    shouldLoadLocaleFromCDN = APP_CONFIG.shouldLoadLocaleFromCDN,
    localisationCdnBaseUrl
}: LoadI18NOptions = DEFAULT_LOCALE_OPTIONS): Promise<{ locale: string }> {
    if (shouldLoadLocaleFromCDN) {
        console.debug('loadI18n() loading from cdn', locale, shouldLoadLocaleFromCDN);

        if (typeof localisationCdnBaseUrl !== 'string' || localisationCdnBaseUrl.length === 0) {
            // if we are in production and this doesn't work there is no bundled fallback.
            console.error('loadI18n() unable to fetch localisationCdnBaseUrl', localisationCdnBaseUrl, locale, shouldLoadLocaleFromCDN);

            throw new Error('loadI18n() unable to fetch LocalisationCdnBaseUrl from configuration');
        }

        console.debug('loadI18n() LocalisationCdnBaseUrl', shouldLoadLocaleFromCDN);

        return loadMessageCatalog({
            locale,
            baseUrl: localisationCdnBaseUrl
        });
    } else {
        // In development you can just use a the local json generated from the lingui tooling,
        console.debug('loadI18n() loading from local bundle');

        const catalog = await import(`../../../locale/${locale}/messages`);

        dynamicActivate({
            locale,
            messages: catalog.messages
        });

        return { locale };
    }
}

export type ActivateOptions = {
    locale?: string
    messages: Messages
};

/**
 * This is based off the official recommendation in lingui to dynamically load locale data
 * and the existing code snippet in 3p projects in the function loadCatalog.
 *
 * @see https://lingui.js.org/guides/dynamic-loading-catalogs.html
 */
export function dynamicActivate ({
    locale = APP_CONFIG.defaultLocale, messages
}: ActivateOptions) {
    i18n.load(locale, messages);
    i18n.loadLocaleData(DEFAULT_LOCALE_DATA);
    i18n.activate(locale);
}

type LoadLocaleDataOptions = {
    locale: string
    baseUrl: string
};

async function loadMessageCatalog (options: LoadLocaleDataOptions): Promise<{ locale: string }> {
    try {
        return loadAndActivateRemoteLocaleData(options)
            .catch((error: unknown) => tryLoadFallbackLocale(options, error));
    } catch (error) {
        return tryLoadFallbackLocale(options, error);
    }
}

function tryLoadFallbackLocale (options: LoadLocaleDataOptions, sourceError: unknown): Promise<{ locale: string }> {
    const failureMessage = `tryLoadFallbackLocale() - received an error loading ${options.locale} no possible replacement.`;

    if (options.locale !== APP_CONFIG.defaultLocale) {
        try {
            console.debug('tryLoadFallbackLocale() loading fallback due to error', sourceError);

            return loadAndActivateRemoteLocaleData({
                ...options,
                locale: APP_CONFIG.defaultLocale
            }).catch((error: unknown) => {
                console.error(failureMessage, error);

                throw new Error(failureMessage);
            });
        } catch (error) {
            const message = `tryLoadFallbackLocale() - the fallback to ${APP_CONFIG.defaultLocale} failed with error...`;

            console.error(message, error);
            throw new Error(message);
        }
    } else {
        throw new Error(failureMessage);
    }
}

/**
 * Load a message catalog from the CDN with the given baseUrl and locale.
 *
 * The baseUrl is typically given through a 3p config eg key "LocalisationCdnBaseUrl".
 * The data is provided through integration between app.lokalise & webhooks with bitbucket html-locales.
 *
 * @see https://app.lokalise.com/projects
 * @see https://bitbucket.org/mathletics/html-locales
 */
async function loadAndActivateRemoteLocaleData ({
    baseUrl, locale
}: LoadLocaleDataOptions): Promise<{ messages: Messages; locale: string }> {
    const response = await fetch(`${baseUrl}/${locale}.json`);
    const messages: Messages = await response.json();

    dynamicActivate({
        locale,
        messages
    });

    return {
        messages,
        locale
    };
}
